import {
  BarcodeOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Page from "_components/Page";
import { CurrencyFormatter } from "_helpers/formatters";
import {
  addressService,
  materialDetailService,
  stockReceiptService,
  taxTypeService,
} from "_services";
import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  PageHeader,
  Popconfirm,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Statistic,
} from "antd";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Stepper from "./Stepper";
import TabMenu from "./TabMenu";
import { StockReceiptOrderStage } from "_helpers";
const TiepSummary = ({ history, match }) => {
  const { id } = match.params;
  const { path } = match;
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);
  const [order, setOrder] = useState({});
  const [selectedGrnLineKeys, setSelectedGrnLineKeys] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState(null);
  const [lineItemMap, setLineItemMap] = useState(new Map());
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [receiptStage, setReceiptStage] = useState("OPEN");
  const [taxTypes, setTaxTypes] = useState([]);
  const { Option } = Select;
  const { Text } = Typography;

  useEffect(() => {
    getStockReceipt();
    getTaxTypes();
  }, [receiptStage]);

  const getTaxTypes = () => {
    taxTypeService
      .getAll()
      .then((data) => setTaxTypes(data.content))
      .catch((error) => message.error(error));
  };

  const getStockReceipt = () => {
    setLoading(true);
    stockReceiptService
      .getByOrderId(id)
      .then((data) => {
        setOrder(data.content);
        setReceiptStage(data.content?.orderStage ?? "OPEN");
        onFill(data.content);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  };

  const onFill = (data) => {
    form.setFieldsValue(data);
    form.setFieldValue(
      "receiptType",
      data?.receiptType === "TIEP" ? "DIRECT" : "INDIRECT"
    );
  };

  const onDispose = async () => {
    const data = form.getFieldValue("quantity");

    const payload = {
      disposeList: Object.entries(data)
        .filter(([_, quantity]) => quantity > 0)
        .map(([stockItemId, quantity]) => ({
          stockItemId: stockItemId,
          quantity: quantity,
        })),
    };

    try {
      setLoading(true);
      await stockReceiptService.dispose(id, payload);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    history.push("/stock-receipts");
  };

  const getActionButtons = () => {
    let buttons = [];

    if (order.orderStage == "COMPLETE") {
      buttons.push(
        <Popconfirm
          title={`Are you sure to dispose it?`}
          onConfirm={onDispose}
          key="con-complete"
          //disabled={addedStocks.length === 0}
        >
          <Button
            key="btn-complete"
            htmlType="button"
            loading={loadingStatusUpdate}
          >
            Dispose
          </Button>
        </Popconfirm>
      );
    }
    buttons.push(
      <Button key="btn-cancel" htmlType="button" onClick={onCancel}>
        Cancel
      </Button>
    );
    return buttons;
  };

  const getLineColumns = () => {
    let columns = [];

    columns.push({
      title: "HS Code",
      key: "stockTypeCode",
      width: "6%",
      dataIndex: "stockTypeCode",
    });

    columns.push({
      title: "Product Code",
      dataIndex: "secondaryCode",
      key: "secondaryCode",
      width: "8%",
      align: "center",
      render: (text, record) => {
        return (
          <Tooltip placeholder="topLeft" title={text}>
            {text.substring(0, 8)}
          </Tooltip>
        );
      },
    });

    columns.push({
      title: "Item No#",
      width: "4%",
      dataIndex: "itemNumber",
      key: "itemNumber",
      align: "center",
    });

    columns.push({
      title: "Description",
      key: "stockTypeName",
      dataIndex: "stockTypeName",
      width: "15%",
      ellipsis: {
        showTitle: false,
      },
    });

    columns.push({
      title: "Package Type",
      dataIndex: ["receivedPackingTypeName"],
      key: "receivedPackingTypeName",
      align: "right",
    });

    columns.push({
      title: "Packages",
      width: "5%",
      dataIndex: "tiepPackageQuantity",
      key: "tiepPackageQuantity",
      align: "right",
    });

    columns.push({
      title: "UOM",
      width: "5%",
      dataIndex: ["receivedUomName"],
      key: "receivedUomName",
      align: "center",
    });

    columns.push({
      title: "units",
      width: "5%",
      dataIndex: ["tiepQuantityPerPackage"],
      key: "tiepQuantityPerPackage",
      align: "center",
    });

    columns.push({
      title: "Nett Weight(Kg)",
      width: "5%",
      dataIndex: ["netWeight"],
      key: "netWeight",
      align: "center",
    });

    columns.push({
      title: "VAT",
      dataIndex: "totalVatAmount",
      key: "totalVatAmount",
      align: "right",
      render: (text, record) => <span>{CurrencyFormatter(text)}</span>,
    });

    columns.push({
      title: "Liability",
      dataIndex: "totalLiabilityAmount",
      key: "totalLiabilityAmount",
      align: "right",
      render: (text, record) => CurrencyFormatter(text ?? 0),
    });

    taxTypes.forEach((taxType) => {
      columns.push({
        title: taxType.name,
        dataIndex: "stockReceiptLineTaxes",
        align: "right",
        key: taxType.code,
        render: (text, record) => {
          const taxEntry = record.stockReceiptLineTaxes?.find(
            (tax) => tax?.taxType?.code === taxType.code
          );
          return <b>{CurrencyFormatter(taxEntry?.taxAmount ?? 0)}</b>;
        },
      });
    });

    return columns;
  };

  const getStockItemColumns = () => {
    let columns = [];

    columns.push({
      title: "HS Code",
      dataIndex: "stockType",
      key: "stockType",
      width: "10%",
      render: (text, record) => {
        return record.stockType && record.stockType.code;
      },
    });

    columns.push({
      title: "Material Code",
      dataIndex: "stockType.secondaryCode",
      width: "10%",
      key: "stockTypeSecondaryCode",
      render: (text, record) => {
        return (
          <Tooltip
            placeholder="topLeft"
            title={record?.stockType?.secondaryCode?.toString()}
          >
            {record?.stockType?.secondaryCode?.toString().substring(0, 8)}
          </Tooltip>
        );
      },
    });

    columns.push({
      title: "Description",
      key: "stockTypeName",
      width: "25%",
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        return (
          <Tooltip placeholder="topLeft" title={record.stockType.name}>
            {record.stockType.name}
          </Tooltip>
        );
      },
    });

    columns.push({
      title: "Recv Date",
      dataIndex: "receivedDate",
      key: "receivedDate",
      width: "10%",
    });

    columns.push({
      title: "Export Number",
      key: "issueNumber",
      width: "10%",
      render: (text, record) => {
        return `${record?.issueNumber ?? ""} (${
          record?.customRefNumber ?? ""
        })`;
      },
    });

    columns.push({
      title: "Current Quantity",
      dataIndex: "tiepQuantityPerMaterial",
      key: "tiepQuantityPerMaterial",
      width: "10%",
    });

    columns.push({
      title: "Remove Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      width: "10%",
      render: (text, record) => {
        return (
          <Form.Item
            name={["quantity", record.id]}
            key={record.id}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            hasFeedback
            initialValue={0}
          >
            <InputNumber
              placeholder="Quantity of Items"
              style={{ width: "100%" }}
            />
          </Form.Item>
        );
      },
    });

    columns.push({
      title: "Status",
      dataIndex: "mode",
      key: "mode",
      align: "center",
      width: "7%",

      render: (text) => {
        let color = "";
        if (text === "AVAILABLE") {
          color = "#87d068";
        }
        if (text === "REQUESTED") {
          color = "#2db7f5";
        }
        if (text === "ISSUED") {
          color = "#108ee9";
        }
        if (text === "DELIVERED") {
          color = "#f50";
        }
        if (text === "LOADED") {
          color = "#87d068";
        }
        if (text === "READY") {
          color = "#87d068";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    });

    return columns;
  };

  const grnLineRowSelection = {
    selectedRowKeys: selectedGrnLineKeys,
    onChange: (selectedGrnLineKeys) => {
      handleTabChange(selectedGrnLineKeys[0]);
    },
  };

  const getLineMaterialSuggestions = async (id, pageNumber, pageSize) => {
    setLoadingLineItems(true);
    let paginationParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

    try {
      setLoadingLineItems(true);

      const result = await stockReceiptService.getSuggestionNew(
        id,
        paginationParams
      );
      setLineItemMap(lineItemMap.set(id, result.content));
      setTotalRecords(result.pagination.totalRecords);
      setPageNumber(result.pagination.pageNumber);
      setPageSize(result.pagination.pageSize);
    } catch (error) {
      setError(error);
    } finally {
      setLoadingLineItems(false);
    }
  };

  const handleTabChange = async (activeKey) => {
    setActiveTabKey(activeKey?.toString());
    setSelectedGrnLineKeys([activeKey?.toString()]);
    if (!lineItemMap.get(activeKey)) {
      await getLineMaterialSuggestions(activeKey, pageNumber, pageSize);
    }
  };

  const handlePaginateChange = (page, pagesize) => {
    setLoadingLineItems(true);
    getLineMaterialSuggestions(activeTabKey, page, pagesize);
  };

  const getTabContent = (grnLines) => {
    let tabPanes = [];

    grnLines.forEach((line, index) => {
      tabPanes.push(
        <Tabs.TabPane
          tab={`${line.stockTypeCode ?? "Items"} (${
            line.tiepPackageQuantity ?? 0
          })`}
          key={line.id}
        >
          <Spin tip="Loading..." spinning={loadingLineItems}>
            {lineItemMap.get(line.id) && (
              <Table
                bordered
                size="small"
                rowKey="id"
                columns={getStockItemColumns()}
                dataSource={lineItemMap
                  .get(line.id)
                  .filter((item) => item.mode === "AVAILABLE")}
                expandable={{
                  expandedStockItemRender,
                  defaultExpandAllRows: true,
                }}
                pagination={{
                  current: pageNumber,
                  pageSize: pageSize,
                  total: totalRecords,
                  showTotal: (total) => `Total ${total} items`,
                  pageSizeOptions: [10, 20, 50, 100, 200],
                  showSizeChanger: true,
                  onChange: (page, pageSize) =>
                    handlePaginateChange(page, pageSize),
                }}
                summary={(pageData) => {
                  let totalQuantity = 0;
                  let totalItems = 0;

                  pageData.forEach(({ tiepQuantityPerMaterial, mode }) => {
                    if (mode === "AVAILABLE") {
                      totalQuantity += tiepQuantityPerMaterial
                        ? tiepQuantityPerMaterial
                        : 0;
                      totalItems += 1;
                    }
                  });

                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={4}>
                          <Text strong>Total Quantity</Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell></Table.Summary.Cell>

                        <Table.Summary.Cell align="left">
                          <Text strong style={{ paddingLeft: 12 }}>
                            {totalQuantity}
                          </Text>
                        </Table.Summary.Cell>

                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  );
                }}
              />
            )}
          </Spin>
        </Tabs.TabPane>
      );
    });

    return tabPanes;
  };

  const getColumnsTaxTable = () => {
    const columns = [
      {
        title: "Tax Code",
        dataIndex: "code",
        key: "Code",
        width: "10%",
        align: "center",
      },
      {
        title: "Tax Name",
        dataIndex: "name",
        key: "Name",
        align: "center",
        width: "15%",
      },
      {
        title: "Tax Amount",
        dataIndex: "totalTaxAmount",
        key: "Amount",
        width: "15%",
        align: "center",
        render: (text, record) => CurrencyFormatter(text ?? 0),
      },
    ];

    return columns;
  };

  const expandedStockItemRender = (value) => {
    const getInnerStockColumns = () => {
      let columns = [];

      columns.push({
        title: "Barcode",
        dataIndex: "barcode",
        key: "barcode",
      });
      columns.push({
        title: "Job No.",
        dataIndex: "jobNumber",
        key: "jobNumber",
      });
      columns.push({
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
      });

      return columns;
    };

    return (
      <Table
        rowKey="id"
        columns={getInnerStockColumns()}
        dataSource={value.children}
        pagination={false}
      />
    );
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/stock-receipts">Stock Receipts Search</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`${path}`.replace(":id", id)}>
                {order.receiptNumber} ::{" "}
                <Tag
                  color={`${
                    order.orderStage === "OPEN"
                      ? "#2db7f5"
                      : order.orderStage === "IN_PROGRESS"
                      ? "#108ee9"
                      : "#87d068"
                  }`}
                >
                  {order.orderStage}
                </Tag>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <Stepper status={order?.orderStage ?? "OPEN"} />
          <TabMenu menu="summary" id={id} />
          <Spin tip="Loading..." spinning={loading}>
            <Form
              layout="vertical"
              form={form}
              name="form-create"
              requiredMark={false}
            >
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item name={["customerName"]} label="Supplier">
                    <Input readonly />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name={["storeName"]}
                    label="Store"
                    rules={[{ required: true }]}
                  >
                    <Input readonly />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="receivedDate"
                    label="Receipt date"
                    rules={[{ required: true }]}
                  >
                    <Input readOnly />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="customRefNumber"
                    label="Customs Ref: #"
                    rules={[{ required: true }]}
                  >
                    <Input readOnly />
                  </Form.Item>
                </Col>

                {/* <Col span={6}>
                    <Form.Item
                      name="orderDate"
                      label="Order Date"
                      rules={[{ required: true }]}
                    >
                      <Input readOnly />
                    </Form.Item>
                  </Col> */}
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    name={["customerAddress"]}
                    label="Deliver From"
                    rules={[{ required: true }]}
                  >
                    <Input></Input>
                    {/* <TextArea
                        readOnly
                        rows={5}
                        style={{
                          resize: "none",
                        }}
                      /> */}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={["storeAddress"]}
                    label="Delivery To"
                    rules={[{ required: true }]}
                  >
                    <Input></Input>
                    {/* <Text
                        readOnly
                        //rows={5}
                        style={{
                          resize: "none",
                        }}
                      /> */}
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="receiptNumber"
                    label="Receipt No"
                    rules={[{ required: true }]}
                  >
                    <Input readonly />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="receiptType" label="Receipt Type">
                    <Input
                      readOnly
                      disabled
                      style={{
                        fontSize: "1.3rem",
                        border: "0px",
                        fontWeight: "bold",
                      }}
                      variant="borderless"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {order && (
                <Row gutter={[24, 24]}>
                  <Col span={8}>
                    <Table
                      size="small"
                      pagination={false}
                      bordered={false}
                      dataSource={order?.stockReceiptTaxViews || []}
                      columns={getColumnsTaxTable()}
                      rowKey={(record) => record.code || Math.random()}
                    />
                  </Col>

                  <Col span={8}>
                    <Form.Item name={["customerName"]} label="Supplier">
                      <Input readonly />
                    </Form.Item>
                  </Col>
                  <Col span={8}></Col>
                </Row>
              )}

              <Row gutter={24} style={{ marginTop: 20}}>
                <Col span={6}>
                  <Form.Item name="totalVatAmount" label="Total VAT">
                    <Statistic precision={2} prefix="LKR" />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="totalLiabilityAmount"
                    label="Total Liability"
                  >
                    <Statistic precision={2} prefix="LKR" />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="totalTaxAmount" label="Total Amount">
                    <Statistic precision={2} prefix="LKR" />
                  </Form.Item>
                </Col>

                <Col span={6}></Col>
              </Row>
              {order?.receiptLines?.length > 0 && (
                <>
                  <Row gutter={24}>
                    <Table
                      bordered
                      pagination={false}
                      columns={getLineColumns()}
                      rowKey="id"
                      dataSource={order.receiptLines}
                      //expandable={{ expandedRowRender, defaultExpandAllRows: true }}
                      rowSelection={{
                        type: "radio",
                        ...grnLineRowSelection,
                      }}
                      rowClassName={(record) =>
                        record?.quantityPerItem > 1
                          ? "ant-tag-green"
                          : "ant-tag-amber"
                      }
                      size="small"
                      scroll={{ x: 1800 }}
                    />
                  </Row>

                  {order?.orderStage == StockReceiptOrderStage.COMPLETE ? (
                    <>
                      <Divider orientation="left">Stock Items</Divider>
                      <Tabs
                        onChange={handleTabChange}
                        activeKey={activeTabKey}
                        type="card"
                      >
                        {getTabContent(order.receiptLines)}
                      </Tabs>
                    </>
                  ) : (
                    ""
                  )}
                </>
              )}
            </Form>
          </Spin>
        </Page>
      </PageHeader>
    </div>
  );
};

export { TiepSummary };
